import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { Loader2, MapPin } from 'lucide-react';

/**
 * Address autocomplete input backed by /api/lookup/address (Nominatim).
 * When user picks a suggestion, calls onSelect({ street, number, postal_code, city, country, lat, lng })
 * and closes the dropdown. Free-text edits still update the street via onChange.
 */
const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Straatnaam',
  required = false,
  testId = 'address-autocomplete',
  cityHint = '',
  postcodeHint = '',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const skipNextFetchRef = useRef(false);

  const fetchSuggestions = useCallback(async (query) => {
    const q = (query || '').trim();
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }
    // Add city / postcode context if present, improves precision
    const parts = [q];
    if (postcodeHint) parts.push(postcodeHint);
    if (cityHint) parts.push(cityHint);
    const fullQ = parts.join(' ');
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/lookup/address?q=${encodeURIComponent(fullQ)}&limit=6`;
      const resp = await fetch(url);
      const data = await resp.json();
      // Deduplicate by display_name
      const seen = new Set();
      const unique = (data.results || []).filter((r) => {
        const key = `${r.street}|${r.number}|${r.postal_code}|${r.city}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setSuggestions(unique);
    } catch (err) {
      console.error('Address lookup failed', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [cityHint, postcodeHint]);

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, fetchSuggestions]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (s) => {
    skipNextFetchRef.current = true;
    setOpen(false);
    setSuggestions([]);
    setHighlightIdx(-1);
    if (onSelect) onSelect(s);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((idx) => Math.min(idx + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((idx) => Math.max(idx - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        data-testid={testId}
      />
      {loading && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      )}
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-md border bg-popover shadow-lg"
          data-testid={`${testId}-dropdown`}
        >
          {suggestions.map((s, idx) => (
            <button
              type="button"
              key={`${s.street}-${s.postal_code}-${s.city}-${idx}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(s);
              }}
              onMouseEnter={() => setHighlightIdx(idx)}
              className={`w-full text-left px-3 py-2 flex items-start gap-2 text-sm hover:bg-muted transition-colors ${
                highlightIdx === idx ? 'bg-muted' : ''
              }`}
              data-testid={`${testId}-option-${idx}`}
            >
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">
                  {s.street}
                  {s.number ? ` ${s.number}` : ''}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {s.postal_code} {s.city}
                  {s.country && s.country !== 'Belgium' && s.country !== 'België' ? `, ${s.country}` : ''}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
